/**
 * allauth headless flow helpers. Dependency-free so node:test can import them
 * (see flows.test.js); allauth.js re-exports everything here.
 */

export const AuthProcess = Object.freeze({
    LOGIN: 'login',
    CONNECT: 'connect'
})

export const Flows = Object.freeze({
    VERIFY_EMAIL: 'verify_email',
    LOGIN: 'login',
    LOGIN_BY_CODE: 'login_by_code',
    SIGNUP: 'signup',
    PROVIDER_REDIRECT: 'provider_redirect',
    PROVIDER_SIGNUP: 'provider_signup',
    PROVIDER_TOKEN: 'provider_token',
    MFA_AUTHENTICATE: 'mfa_authenticate',
    REAUTHENTICATE: 'reauthenticate',
    MFA_REAUTHENTICATE: 'mfa_reauthenticate'
})

export const AuthenticatorType = Object.freeze({
    TOTP: 'totp',
    RECOVERY_CODES: 'recovery_codes',
    WEBAUTHN: 'webauthn'
})

export const MFA_AUTHENTICATE_TOTP_PATH = '/account/mfa/authenticate/totp'
export const MFA_AUTHENTICATE_WEBAUTHN_PATH =
    '/account/mfa/authenticate/webauthn'
export const MFA_AUTHENTICATE_RECOVERY_CODES_PATH =
    '/account/mfa/authenticate/recovery-codes'
export const REAUTHENTICATE_PATH = '/account/reauthenticate'

// The MFA sign-in step, one page per kind of authenticator, in the order the
// SPA prefers them: a key tap is quickest, a recovery code is the last resort.
const MFA_STEP_PAGES = [
    {
        type: AuthenticatorType.WEBAUTHN,
        path: MFA_AUTHENTICATE_WEBAUTHN_PATH,
        label: 'Use your security key or passkey'
    },
    {
        type: AuthenticatorType.TOTP,
        path: MFA_AUTHENTICATE_TOTP_PATH,
        label: 'Use your authenticator app'
    },
    {
        type: AuthenticatorType.RECOVERY_CODES,
        path: MFA_AUTHENTICATE_RECOVERY_CODES_PATH,
        label: 'Use a recovery code'
    }
]

/** The page for the MFA sign-in step, given the types allauth says the user has. */
export function mfaAuthenticatePath(types) {
    const available = Array.isArray(types) ? types : []
    const page = MFA_STEP_PAGES.find((p) => available.includes(p.type))
    return page ? page.path : MFA_AUTHENTICATE_TOTP_PATH
}

/**
 * The other ways to finish the MFA step, as links, for the page showing
 * `currentType`. Unknown `types` (the session could not be read) offers all
 * of them rather than leaving someone stuck.
 */
export function mfaAlternatives(types, currentType) {
    const available = Array.isArray(types) ? types : null
    return MFA_STEP_PAGES.filter(
        (p) =>
            p.type !== currentType &&
            (available === null || available.includes(p.type))
    ).map(({ type, path, label }) => ({ type, to: path, label }))
}

/** The pending mfa_authenticate flow's authenticator types, or null. */
export function pendingMfaTypes(data) {
    const flow = data?.data?.flows?.find(
        (f) => f.id === Flows.MFA_AUTHENTICATE && f.is_pending
    )
    return flow?.types ?? null
}

/**
 * Whether allauth refused a call because the sign-in is not recent enough.
 * It answers 401 for a signed-in user (meta.is_authenticated stays true) and
 * lists the ways to confirm (reauthenticate, mfa_reauthenticate), none of
 * them marked pending.
 */
export function needsReauthentication(result) {
    return result?.status === 401 && result?.meta?.is_authenticated === true
}

/**
 * Returns the current pending flow from a session/auth response, if any.
 */
export function getPendingFlow(data) {
    if (!data?.data?.flows) return null
    return data.data.flows.find((f) => f.is_pending) || null
}

/**
 * Determine the URL to navigate to for a pending flow.
 *
 * MFA during sign-in goes to the security key page, the authenticator app
 * page or the recovery code page, whichever the account has first in that
 * order; each page links to the others the account has.
 */
export function pendingFlowUrl(flow) {
    if (!flow) return null
    switch (flow.id) {
        case Flows.VERIFY_EMAIL:
            return '/resend-verification'
        case Flows.MFA_AUTHENTICATE:
            return mfaAuthenticatePath(flow.types)
        case Flows.PROVIDER_SIGNUP:
            return '/account/provider/signup'
        case Flows.REAUTHENTICATE:
        case Flows.MFA_REAUTHENTICATE:
            return REAUTHENTICATE_PATH
        default:
            return null
    }
}

/**
 * If an allauth response carries a pending flow with a page of its own, or
 * asks the user to confirm who they are, go there and return true. Used after
 * calls that can come back asking the user to sign in again first (allauth
 * answers 401 rather than throwing), so the page does not mistake that for
 * success.
 */
export function followPendingFlow(result, navigate) {
    const url =
        pendingFlowUrl(getPendingFlow(result)) ||
        (needsReauthentication(result) ? REAUTHENTICATE_PATH : null)
    if (!url) return false
    navigate(url)
    return true
}
